import { useState } from 'react';
import {
  Button,
  InlineStack,
  Panel,
  PanelActions,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Paragraph,
  Strong,
  Text,
  Tooltip,
  useConfirmationModal,
} from '@customerio/pluma-components/react';
import { formatLongTimestamp } from '@/utils/calendar-timestamps';
import {
  channelValueOf,
  DeliveryStateTag,
  isExpiredEventTriggeredCampaign,
  ResendButton,
  useDeliveryActions,
  useDeliveryRelatedModel,
  useDeliveryRowPermission,
  type DeliveryRowPermission,
} from '@/features/deliveries';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import {
  getDeliveryState,
  getDisplayState,
  getDisplayStateTimestamp,
  hasDeliveryError,
} from '@/services/db/delivery/utils';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';
import { orderedPushPlatforms, resolvePushPreview } from '../utils/push-payload';

/**
 * Retry only resets failed/undeliverable metrics server-side; the other states
 * covered by `canRetryDelivery` render the button disabled with this reason
 * (Ember `retryUnsupportedReason`).
 */
const RETRY_UNSUPPORTED_REASON: Record<string, string> = {
  bounced: "This message bounced and can't be retried in place. Use Resend to send it again.",
  spammed: "This message was marked as spam and can't be retried in place. Use Resend to send it again.",
  suppressed: "This recipient is suppressed, so this message can't be retried or resent.",
};

/**
 * The delivery's title (Ember `displaySubject`). A custom-payload push shows its message
 * title (the first non-empty per-platform title — Ember `findFirstNonEmptyTitle` over the
 * template body); otherwise the delivery's subject, then the rendered detail's, then a
 * placeholder.
 *
 * We derive the push title from the rendered `detail.body`, which retention blanks, so we
 * skip it when `hide_body` — the same gate the message preview uses — to avoid surfacing
 * suppressed content in the header. (Ember reads the un-gated message template instead.)
 */
function displaySubject(delivery: Delivery, detail: DeliveryDetail): string {
  if (channelValueOf(delivery.action_type) === 'push' && detail.hide_body !== true) {
    const pushTitle = firstNonEmptyPushTitle(delivery, detail);
    if (!isEmptyString(pushTitle)) return pushTitle;
  }
  if (!isEmptyString(delivery.subject)) return delivery.subject;
  if (!isEmptyString(detail.subject)) return detail.subject;
  return '(No subject)';
}

/**
 * The first non-empty per-platform title of a custom-payload push (Ember `findFirstNonEmptyTitle`).
 * We read the rendered `detail.body` — the same per-platform payload the Message tab parses — and walk
 * it from the delivery's first sent platform, so the header shows the title of the platform the preview
 * opens on. `''` for a simple push or a body without platform titles.
 */
function firstNonEmptyPushTitle(delivery: Delivery, detail: DeliveryDetail): string {
  const preview = resolvePushPreview(detail);
  for (const platform of orderedPushPlatforms(delivery, preview.platformsWithBody)) {
    const title = preview.byPlatform[platform]?.title;
    if (!isEmptyString(title)) return title;
  }
  return '';
}

type DeliveryPreviewHeaderProps = { delivery: Delivery; detail: DeliveryDetail };

const RETRYABLE_STATES = new Set(['failed', 'bounced', 'spammed', 'suppressed', 'undeliverable']);
const RESENDABLE_STATES = new Set(['sent', 'delivered', 'bounced', 'spammed']);

/** Per-button visibility + disabled state, derived from the delivery (Ember preview getters). */
type HeaderActionState = {
  isReadOnly: boolean;
  hasSendPermission: boolean;
  isAttempted: boolean;
  canDelete: boolean;
  canEdit: boolean;
  showSend: boolean;
  sendDisabled: boolean;
  canRetry: boolean;
  canResend: boolean;
  /** Event-triggered campaign delivery past the 30-day events-retention window — disables Resend. */
  resendExpired: boolean;
  state: ReturnType<typeof getDeliveryState>;
};

function useHeaderActions(delivery: Delivery, detail: DeliveryDetail): HeaderActionState {
  const { model, isLoading } = useDeliveryRelatedModel(delivery);
  // Read-only when the user lacks the parent message's edit permission; resend
  // carries the extra `deliveries/send` gate. Same source the Outbox list uses.
  const { hasReadOnlyPermission, hasSendPermission } = useDeliveryRowPermission(delivery, model);

  const state = getDeliveryState(delivery);
  // Gate every action until the parent model resolves: while the campaign query
  // is in flight `model.kind` is `'none'`, so the permission hook falls back to
  // the campaigns gate (wrong for an api-triggered broadcast). The list row gates
  // the same buttons on `isLoading` (delivery-row-expansion `actionsLoading`).
  const isReadOnly = hasReadOnlyPermission || isLoading;
  const isDraftCampaign = model.kind === 'campaign' && state === 'drafted';
  const isAttempted = state === 'attempted';

  return {
    isReadOnly,
    hasSendPermission,
    isAttempted,
    canDelete: isDraftCampaign,
    canEdit: isDraftCampaign && delivery.action_type === 'email_action',
    showSend: isAttempted || state === 'drafted',
    sendDisabled: isReadOnly || isAttempted || hasDeliveryError(delivery, detail),
    canRetry: state != null && RETRYABLE_STATES.has(state),
    canResend: state != null && RESENDABLE_STATES.has(state),
    resendExpired: isExpiredEventTriggeredCampaign(delivery, model),
    state,
  };
}

/**
 * The delivery preview header (title, per-action buttons, state tag + timestamp),
 * rendered above the message preview. The `retry_send_*` / redeliver analytics
 * events stay deferred (FPS-3194).
 */

export function DeliveryPreviewHeader({ delivery, detail }: DeliveryPreviewHeaderProps) {
  const workspaceId = requireWorkspaceId();
  const taxonomy = useProductTaxonomy();
  const buttons = useHeaderActions(delivery, detail);
  const actions = useDeliveryActions({ deliveries: [delivery], workspaceId });
  const displayStateTimestamp = getDisplayStateTimestamp(delivery);
  const resendPermission: DeliveryRowPermission = {
    hasReadOnlyPermission: buttons.isReadOnly,
    hasSendPermission: buttons.hasSendPermission,
  };

  return (
    <Panel as="section">
      <PanelHeader>
        <PanelTitle className="fs-mask">{displaySubject(delivery, detail)}</PanelTitle>

        <PanelActions>
          {/* `DeleteDraftButton` supplies its own confirm dialog, so it uses the direct
              (non-confirming) `onRowDelete` to avoid a double confirmation. */}
          {buttons.canDelete ? (
            <DeleteDraftButton delivery={delivery} isDisabled={buttons.isReadOnly} onDelete={actions.onRowDelete} />
          ) : null}

          {buttons.canEdit ? (
            <Button
              variant="secondary"
              isDisabled={buttons.isReadOnly}
              // Taxonomy-aware draft-edit URL (Ember-owned): the `outbox`↔`message_activity`
              // segment swaps with the rename, so build the base from the taxonomy route.
              href={`${taxonomy.route('messageActivity.drafts').replace(':workspaceId', String(workspaceId))}/${delivery.id}/edit`}
            >
              Edit
            </Button>
          ) : null}

          {buttons.canResend ? (
            <ResendButton
              variant="primary"
              size="md"
              delivery={delivery}
              permission={resendPermission}
              hideBody={detail.hide_body === true}
              isExpired={buttons.resendExpired}
              onRedeliver={(tracked) => actions.onBulkRedeliver([delivery.id], { tracked })}
            />
          ) : null}

          {buttons.showSend ? (
            <SendButton
              isDisabled={buttons.sendDisabled}
              isAttempted={buttons.isAttempted}
              isPending={delivery.pendingSend === true}
              onSend={() => actions.onBulkSend([delivery.id])}
            />
          ) : null}

          {buttons.canRetry ? (
            <RetryButton
              state={buttons.state}
              isReadOnly={buttons.isReadOnly}
              isPending={delivery.pendingRetry === true}
              onRetry={() => actions.onRowRetry([delivery.id])}
            />
          ) : null}
        </PanelActions>

        <PanelDescription as="div">
          <InlineStack alignBlock="center" gap="100">
            <DeliveryStateTag state={getDisplayState(delivery) ?? ''} channel={channelValueOf(delivery.action_type)} />
            {/* 0 when the state's value isn't a usable timestamp (a count-shaped metric) —
                show the tag alone rather than a 1 Jan 1970 date. */}
            {displayStateTimestamp > 0 ? (
              <Text size="s" color="subtle">
                {formatLongTimestamp(displayStateTimestamp)}
              </Text>
            ) : null}
          </InlineStack>
        </PanelDescription>
      </PanelHeader>
    </Panel>
  );
}

type DeleteDraftButtonProps = {
  delivery: Delivery;
  isDisabled: boolean;
  onDelete: (ids: string[]) => void;
};

/** Delete a drafted delivery behind a confirm dialog (Ember `CioDeleteButton`). */
function DeleteDraftButton({ delivery, isDisabled, onDelete }: DeleteDraftButtonProps) {
  const confirm = useConfirmationModal();
  // Latch once the confirm resolves so a second confirmation can't re-fire the
  // delete before `pendingDelete` lands; the cache-backed flag holds it disabled
  // after a remount (mirrors the Outbox list row).
  const [fired, setFired] = useState(false);

  const onClick = async () => {
    const confirmed = await confirm({
      title: 'Delete draft',
      isDanger: true,
      confirmLabel: 'Yes, delete draft',
      message: (
        <>
          <Paragraph mb="200">
            Heads up! You’re about to delete this draft. This <Strong>cannot</Strong> be undone.
          </Paragraph>
          <Paragraph>Are you sure?</Paragraph>
        </>
      ),
    });
    if (!confirmed) return;
    setFired(true);
    onDelete([delivery.id]);
  };

  return (
    <Button
      variant="secondary"
      isDanger
      isDisabled={isDisabled || fired || delivery.pendingDelete === true}
      onClick={() => void onClick()}
    >
      Delete draft
    </Button>
  );
}

type SendButtonProps = {
  isDisabled: boolean;
  isAttempted: boolean;
  /** Optimistic `delivery.pendingSend` — keeps Send disabled across a remount. */
  isPending: boolean;
  onSend: () => void;
};

/** Send/queue an attempted or drafted delivery; attempted rows show the auto-retry note. */
function SendButton({ isDisabled, isAttempted, isPending, onSend }: SendButtonProps) {
  // Latch on the first click so a repeat click can't re-POST the send before the
  // optimistic `pendingSend` flag lands (mirrors the Outbox list row).
  const [fired, setFired] = useState(false);
  const onClick = () => {
    setFired(true);
    onSend();
  };
  return (
    <Tooltip
      isCustom
      isDisabled={!isAttempted}
      content="Attempted messages will be retried automatically."
      placement="top"
    >
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="primary"
          isDisabled={isDisabled || fired || isPending}
          onClick={onClick}
        >
          Send message
        </Button>
      )}
    </Tooltip>
  );
}

type RetryButtonProps = {
  state: ReturnType<typeof getDeliveryState>;
  isReadOnly: boolean;
  /** Optimistic `delivery.pendingRetry` — keeps Retry disabled across a remount. */
  isPending: boolean;
  onRetry: () => void;
};

/** Retry a failed/undeliverable delivery; the other retryable states render disabled with a reason. */
function RetryButton({ state, isReadOnly, isPending, onRetry }: RetryButtonProps) {
  // Latch on the first click so a repeat click can't re-POST the retry before the
  // optimistic `pendingRetry` flag lands (mirrors the Outbox list row).
  const [fired, setFired] = useState(false);
  const reason = state == null ? null : (RETRY_UNSUPPORTED_REASON[state] ?? null);
  const onClick = () => {
    setFired(true);
    onRetry();
  };
  return (
    <Tooltip isCustom isDisabled={reason == null} content={reason ?? ''} placement="top">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="secondary"
          isDisabled={isReadOnly || reason != null || fired || isPending}
          onClick={onClick}
        >
          Retry
        </Button>
      )}
    </Tooltip>
  );
}
